<template>
    <div class="container-fluid">
        <Loading v-if="!loaded"></Loading>
        <div v-else>
            <!-- TITLE -->
            <div class="mt-2 tabName">
                <h3>{{ $t('custom_medications') }}</h3>
            </div>
            <!-- CREATE BUTTON -->
            <div class="row mt-3">
                <div class="col-sm-8">
                    <router-link :to="{ name: 'customMedications.create' }" class="btn btn-md btn-success">
                        {{ $t('create_custom_medication') }}
                    </router-link>
                </div>
            </div>
            <!-- TABLE -->
            <div class="row mt-3" v-if="customMedications.length">
                <div class="col">
                    <b-table class="mt-4" striped hover :fields="tableFields" :items="customMedications" sort-by="vnr">
                        <template #cell(package_size)="data">
                            {{ data.item.package_size }} {{ data.item.form }}
                        </template>
                        <template #cell(drug)="data">
                            {{ data.item.drug ? $t('yes').toLowerCase() : $t('no').toLowerCase() }}
                        </template>
                        <template #cell(button_activate)="data">
                            <button
                                :class="data.item.active ? 'btn btn-sm btn-secondary' : 'btn btn-sm btn-success'"
                                v-on:click="() => handleChangeActiveState(data.item.vnr)"
                                :disabled="submitClicked"
                            >
                                {{ data.item.active ? $t('deactivate') : $t('activate') }}
                            </button>
                        </template>
                        <template #cell(button_edit)="data">
                            <div
                                v-b-tooltip="{
                                    title: $t('custom_medication_cannot_edit_in_use'),
                                    disabled: data.item.editable
                                }"
                            >
                                <button
                                    class="btn btn-sm btn-info ml-sm-0"
                                    v-on:click="
                                        $router.push({ name: 'customMedications.edit', params: { vnr: data.item.vnr } })
                                    "
                                    :disabled="!data.item.editable || submitClicked"
                                >
                                    {{ $t('edit') }}
                                </button>
                            </div>
                        </template>
                        <template #cell(button_delete)="data">
                            <div
                                v-b-tooltip="{
                                    title: $t('custom_medication_cannot_delete_in_use'),
                                    disabled: data.item.editable
                                }"
                            >
                                <button
                                    class="btn btn-sm btn-danger"
                                    v-on:click="() => handleDeleteCustomMedicationClick(data.item.vnr)"
                                    :disabled="!data.item.editable || submitClicked"
                                >
                                    {{ $t('delete') }}
                                </button>
                            </div>
                        </template>
                    </b-table>
                </div>
            </div>
        </div>
        <!-- MODAL: ARE YOU SURE YOU WANT TO DELETE CUSTOM MEDICATION? -->
        <b-modal
            id="deleteCustomMedModal"
            centered
            :title="$t('delete_custom_medication_are_you_sure_title')"
            hide-footer
        >
            <div class="row">
                <div class="col">
                    <p>{{ $t('delete_custom_medication_are_you_sure_text') }}</p>
                </div>
            </div>
            <div class="d-flex row mt-2">
                <div class="d-flex col justify-content-center">
                    <button class="btn btn-primary mr-4" @click="deleteCustomMedication">
                        {{ $t('ok') }}
                    </button>
                    <button class="btn btn-secondary" @click="handleDeleteCustomMedicationCancel">
                        {{ $t('cancel') }}
                    </button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import Loading from '@/views/partial/Loading';
import {
    deleteCustomMedication,
    getCustomMedications,
    handleAxiosError,
    updateCustomMedicationActiveState
} from '@/services/axiosService';
import { getMedDisplayName } from '@/services/medicationService';

export default {
    name: 'CustomMedications',
    components: { Loading },
    data() {
        return {
            customMedications: [],
            deleteCustomMedicationVnr: null,
            getMedDisplayName,
            errors: [],
            loaded: false,
            submitClicked: false
        };
    },
    async created() {
        await this.getCustomMedications();
        this.loaded = true;
    },
    computed: {
        ...mapState('account', ['status', 'user']),
        tableFields() {
            let tableFields = [
                { key: 'vnr', sortable: true, label: this.$t('vnr'), tdClass: 'align-middle' },
                { key: 'name', sortable: true, label: this.$t('name'), tdClass: 'align-middle' },
                {
                    key: 'company',
                    sortable: true,
                    label: this.$t('marketing_authorization_holder_tworows'),
                    tdClass: 'align-middle'
                },
                { key: 'supplier', sortable: true, label: this.$t('supplier'), tdClass: 'align-middle' },
                {
                    key: 'company_reseller',
                    sortable: true,
                    label: this.$t('reseller_tworows'),
                    tdClass: 'align-middle'
                },
                { key: 'dosage', sortable: true, label: this.$t('dosage_tworows'), tdClass: 'align-middle' },
                { key: 'dosage_form', sortable: true, label: this.$t('dosage_form_tworows'), tdClass: 'align-middle' },
                {
                    key: 'package_size',
                    sortable: true,
                    label: this.$t('package_size_tworows'),
                    tdClass: 'align-middle'
                },
                { key: 'chemical', sortable: true, label: this.$t('chemical'), tdClass: 'align-middle' },
                { key: 'atc', sortable: true, label: this.$t('atc'), tdClass: 'align-middle' },
                { key: 'drug', sortable: true, label: this.$t('drug_tworows'), tdClass: 'align-middle' },
                { key: 'button_activate', sortable: false, label: '', tdClass: 'align-middle' },
                { key: 'button_edit', sortable: false, label: '', tdClass: 'align-middle' },
                { key: 'button_delete', sortable: false, label: '', tdClass: 'align-middle' }
            ];
            return tableFields;
        }
    },
    methods: {
        ...mapActions('account', ['logout']),
        // delete custom medication and update table
        async deleteCustomMedication() {
            try {
                this.$bvModal.hide('deleteCustomMedModal');
                await deleteCustomMedication(this.deleteCustomMedicationVnr);
                this.$toastr.s(this.$t('custom_medication_deleted'));
                this.customMedications = this.customMedications.filter(
                    med => med.vnr !== this.deleteCustomMedicationVnr
                );
            } catch (error) {
                handleAxiosError(error);
            }
            this.handleDeleteCustomMedicationCancel();
        },
        // get custom medications (sorted by medication name and vnr)
        async getCustomMedications() {
            try {
                const medications = await getCustomMedications();
                this.customMedications = _.sortBy(medications, ['medication_name', 'vnr']);
            } catch (error) {
                handleAxiosError(error);
            }
        },
        // handle medication active state change
        async handleChangeActiveState(vnr) {
            try {
                await updateCustomMedicationActiveState(vnr);
                const medIndex = this.customMedications.findIndex(med => med.vnr === vnr);
                this.customMedications[medIndex].active = !this.customMedications[medIndex].active;
                this.$toastr.s(this.$t('custom_medication_updated'));
            } catch (error) {
                handleAxiosError(error);
            }
        },
        // handle medication deletion cancellation
        handleDeleteCustomMedicationCancel() {
            this.submitClicked = false;
            this.deleteCustomMedicationVnr = null;
            this.$bvModal.hide('deleteCustomMedModal'); // hide "are you sure?" modal
        },
        // handle medication deletion click
        handleDeleteCustomMedicationClick(vnr) {
            this.submitClicked = true;
            this.deleteCustomMedicationVnr = vnr;
            this.$bvModal.show('deleteCustomMedModal'); // show "are you sure?" modal
        }
    },
    // handle other "hide modal" actions (background click, esc key press, modal close click)
    mounted() {
        this.$root.$on('bv::modal::hide', bvEvent => {
            if (['backdrop', 'esc', 'headerclose'].includes(bvEvent.trigger)) {
                this.handleDeleteCustomMedicationCancel();
            }
        });
    }
};
</script>
